import { AccessLevel, AccountOptions, Campaign, CampaignStatus, CampaignType } from '@dev/ee-api/ee-api-models';
import { Alert, Container, DialogBody, PrimaryButton, TabsPanel, palette } from '@dev/ee-components';
import { BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { Box, Link } from '@material-ui/core';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import { Route, Switch } from 'react-router-dom';
import { SetCampaignCreatorOpen, getCampaignsStore } from '@/store/campaigns/actions';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import React from 'react';
import { ResendActivationEmail } from '@/store/auth/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowToast } from '@/store/toast/actions';
import { generateIdBasedOnPath } from '@dev/ee-tools';
import { getAccountStore } from '@/store/account/actions';
import { isAccountActivated } from '@/services/Secuirty';
import AutorespondersList from './AutorespondersList';
import CampaignDeleteDialog from './CampaignDeleteDialog';
import { CampaignTabName } from '../../store/campaigns/types';
import CampaignsList from './CampaignsList';
import { CampaignsPaths } from '../../services/dictionaries/Paths';
import DraftsList from './DraftsList';
import Preview from './Preview';
import { StartCampaignCreator } from '../CampaignCreator/Start';
import { getConfigStore } from '../../store';
import { CampaignsProps, ScreenType } from './CampaignsInterface';
import { isFutureScheduledWithAnyDelivered } from './helperFunctions';

export const isCancelCampaignPossible = (campaign?: Campaign): boolean => {
    if (!campaign) {
        return false;
    }

    const { Status: status } = campaign;

    if (campaign.Type === CampaignType.Automated) {
        return status === CampaignStatus.Active;
    } else {
        return status === CampaignStatus.Sending;
    }
};

export const isReactivateCampaignAvailable = (campaign: Campaign): boolean =>
    campaign?.Status === CampaignStatus.Paused;

export const isCampaignsReportAvailable = (campaign: Campaign): boolean =>
    campaign.Status !== CampaignStatus.Active || isFutureScheduledWithAnyDelivered(campaign);

const getScreenIndexFromName = (screenName: string, isAutoresponderTabVisible: boolean): number => {
    switch (screenName) {
        case CampaignTabName.Autoresponders:
            return 1;
        case CampaignTabName.Drafts:
            return isAutoresponderTabVisible ? 2 : 1;
        default:
            return 0;
    }
};

const getScreenName = (screenIndex: number | string): ScreenType => {
    switch (screenIndex) {
        case 1:
        case CampaignTabName.Autoresponders:
            return CampaignTabName.Autoresponders;
        case 2:
        case CampaignTabName.Drafts:
            return CampaignTabName.Drafts;
        default:
            return CampaignTabName.Campaigns;
    }
};

const getScreenUri = (screenIndex: number, isAutoresponderTabVisible: boolean): string => {
    const index = !isAutoresponderTabVisible && screenIndex > 0 ? screenIndex + 1 : screenIndex;

    switch (index) {
        case 1:
            return CampaignsPaths.AutorespondersTab;
        case 2:
            return CampaignsPaths.DratfsTab;
        default:
            return CampaignsPaths.Campaigns.replace(':view?', '');
    }
};

export const Campaigns = (props: CampaignsProps): JSX.Element => {
    const isSavedRef = React.useRef(true);

    const historyBackAction = useHistoryBackAction({
        isSavedRef,
    });
    const { t } = useTranslation();
    const config = useSelector(getConfigStore);
    const account = useSelector(getAccountStore);
    const campaigns = useSelector(getCampaignsStore);
    const { IsExpired, IsUser, ProductType } = account?.GeneralInfo || {};
    const dispatch = useDispatch<any>();

    const onTabsChange = (e: any, clickedTabIndex: number): void => {
        props.history.replace(getScreenUri(clickedTabIndex, isAutoresponderTabVisible()));
    };

    const isAutoresponderTabVisible = (): boolean =>
        account.GeneralInfo?.Options?.includes(AccountOptions.EnableContactHistory);

    const tabNames = (): { label: string }[] => [
        {
            label: t('campaign'),
        },
        ...(isAutoresponderTabVisible()
            ? [
                  {
                      label: t('autoresponder'),
                  },
              ]
            : []),
        {
            label: t('draft'),
        },
    ];

    const handleCreateCampaignButtonClick = () => {
        if (!isAccountActivated()) {
            return openActivateAccountDialog();
        }
        return openCampaignCreator();
    };

    const resendActivationEmail = () => {
        const email = account.GeneralInfo?.UserName;
        try {
            dispatch(ResendActivationEmail?.({ Email: email })).then(() => {
                dispatch(
                    ShowToast({
                        severity: 'info',
                        message: t('activationemailresent2'),
                        duration: 1000,
                    })
                );
            });
        } catch {
            dispatch(
                ShowToast({
                    severity: 'error',
                    message: t('errorinfo'),
                    duration: 3000,
                })
            );
        }
    };

    const openActivateAccountDialog = () => {
        dispatch(
            ShowDialog({
                title: t('accounthasntbeenactivated'),
                cancelLabel: t('cancel'),
                confirmLabel: t('resendactivationemail'),
                body: <DialogBody variant="info">{t('campaign_activateaccount_dialog')}</DialogBody>,
                onClickConfirm: resendActivationEmail,
            })
        );
    };

    const openCampaignCreator = (): void => {
        if (config.isStandalone) {
            dispatch(SetCampaignCreatorOpen(true));
        } else {
            window.location.hash = CampaignsPaths.OldCreateCampaign;
        }
    };

    // eslint-disable-next-line arrow-body-style
    const hasAccessFor = (key: AccessLevel): boolean => {
        return account.GeneralInfo?.AccountAccessLevel?.includes(key);
    };

    const canModifyCampaigns = !IsUser || hasAccessFor(AccessLevel.ModifyCampaigns);

    return (
        <Container>
            <HistoryBackActionContext.Provider value={historyBackAction}>
                <TabsPanel
                    tabBackground={config.isStandalone ? '' : palette.basic.paper}
                    title={t('campaigns')}
                    isMobile={false}
                    value={getScreenIndexFromName(getScreenName(props.match.params.view), isAutoresponderTabVisible())}
                    onChange={onTabsChange}
                    tabs={tabNames()}
                    additionalHeaderContent={
                        canModifyCampaigns && (
                            <PrimaryButton
                                id={generateIdBasedOnPath('create-campaign-button')}
                                text={t('createcampaigncaptalize')}
                                onClick={handleCreateCampaignButtonClick}
                            />
                        )
                    }
                />
                {IsExpired && (
                    <Box mt="24px">
                        <Alert
                            severity="warning"
                            body={
                                <Trans
                                    i18nKey="planexpired_upgradetosending"
                                    components={{
                                        l: (
                                            <Link
                                                href={BillingPaths.BillingPlanList.replace(
                                                    pathsParams.productCode,
                                                    ProductType
                                                )}
                                            />
                                        ),
                                    }}
                                />
                            }
                        />
                    </Box>
                )}
                <Switch>
                    <Route exact component={CampaignsList} path={CampaignsPaths.Campaigns.replace(':view?', '')} />
                    <Route exact component={CampaignsList} path={CampaignsPaths.CampaignsTab} />
                    {isAutoresponderTabVisible() && (
                        <Route exact component={AutorespondersList} path={CampaignsPaths.AutorespondersTab} />
                    )}
                    <Route exact component={DraftsList} path={CampaignsPaths.DratfsTab} />
                </Switch>
                <CampaignDeleteDialog />
                {campaigns.currentPreview.campaign && campaigns.currentPreview.templates?.length && <Preview />}
                {campaigns.isCampaignCreatorOpen && <StartCampaignCreator />}
            </HistoryBackActionContext.Provider>
        </Container>
    );
};
